import { useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import { RefreshCw, Filter, Search, History as HistoryIcon, ChevronDown } from "lucide-react";
import { adminFetchAuditLog } from "@/lib/api";

const ENTITY_OPTIONS = [
  "all", "crm_projects", "project_invoices", "project_payments",
  "project_expenses", "vendor_payments", "reachvel_payments", "non_project_invoices",
  "leads", "vendors", "crm_tasks", "client", "comment",
];
const ACTOR_OPTIONS = ["all", "admin", "client", "system"];
const ACTION_OPTIONS = ["all", "create", "update", "delete", "upload_file", "login", "finance_unlock"];

const ACTION_TONE = {
  create:           "bg-emerald-100 text-emerald-800",
  update:           "bg-sky-100 text-sky-800",
  delete:           "bg-rose-100 text-rose-800",
  upload_file:      "bg-amber-100 text-amber-800",
  login:            "bg-zinc-100 text-zinc-700",
  finance_unlock:   "bg-violet-100 text-violet-800",
};

function fmtTs(iso) {
  if (!iso) return "—";
  try {
    return new Date(iso).toLocaleString("en-IN", {
      day: "2-digit", month: "short", year: "numeric",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

export default function AuditLog({ token }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [entity, setEntity] = useState("all");
  const [actor, setActor] = useState("all");
  const [action, setAction] = useState("all");
  const [q, setQ] = useState("");
  const [expanded, setExpanded] = useState({});

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 300 };
      if (entity !== "all") params.entity_type = entity;
      if (actor !== "all") params.actor_type = actor;
      if (action !== "all") params.action = action;
      if (q.trim()) params.q = q.trim();
      const data = await adminFetchAuditLog(token, params);
      setRows(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load audit log");
    } finally {
      setLoading(false);
    }
  }, [token, entity, actor, action, q]);

  useEffect(() => { load(); }, [load]);

  return (
    <div data-testid="admin-audit-log">
      <div className="flex items-end justify-between gap-4 mb-6 flex-wrap">
        <div>
          <div className="text-[11px] font-mono uppercase tracking-[0.25em] text-[#ff5722] font-bold flex items-center gap-2">
            <HistoryIcon className="h-3.5 w-3.5" /> Audit Log
          </div>
          <h2 className="mt-1 crm-h text-3xl md:text-4xl text-[#0a0a0a]">Activity history</h2>
          <p className="mt-2 text-sm text-[#4a4a4a]">Every create, update, delete, login &amp; finance unlock is logged here. Use filters to drill in.</p>
        </div>
        <button onClick={load} className="inline-flex items-center gap-2 px-3 py-1.5 text-xs border border-black/15 rounded-full hover:border-[#ff5722] hover:text-[#ff5722]">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
        </button>
      </div>

      <div className="bg-white border border-black/10 px-4 py-3 flex flex-wrap gap-2 items-center mb-3">
        <div className="relative">
          <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-[#9a9a9a]" />
          <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search summary, actor, ID…"
            className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-3 py-1.5 w-64 outline-none focus:border-[#ff5722]" />
        </div>
        <Select icon={<Filter className="h-3.5 w-3.5" />} value={entity} onChange={setEntity} options={ENTITY_OPTIONS} prefix="Entity" />
        <Select icon={<Filter className="h-3.5 w-3.5" />} value={actor}  onChange={setActor}  options={ACTOR_OPTIONS}  prefix="Actor" />
        <Select icon={<Filter className="h-3.5 w-3.5" />} value={action} onChange={setAction} options={ACTION_OPTIONS} prefix="Action" />
        <span className="ml-auto text-[11px] font-mono text-[#4a4a4a]">{rows.length} {rows.length === 1 ? "entry" : "entries"}</span>
      </div>

      <div className="bg-white border border-black/10 overflow-x-auto">
        {loading ? (
          <div className="p-10 text-sm text-[#4a4a4a]">Loading…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-sm text-[#4a4a4a]">No log entries match your filters.</div>
        ) : (
          <table className="w-full text-left min-w-[900px]">
            <thead className="bg-[#f7f6f3] border-b border-black/10 text-[11px] font-mono uppercase tracking-[0.2em] text-[#4a4a4a]">
              <tr>
                <th className="px-4 py-3">When</th>
                <th className="px-4 py-3">Actor</th>
                <th className="px-4 py-3">Action</th>
                <th className="px-4 py-3">Entity</th>
                <th className="px-4 py-3">Summary</th>
                <th className="px-4 py-3 w-10"></th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => {
                const open = !!expanded[r.id];
                const hasDetails = r.meta && Object.keys(r.meta).length > 0;
                return (
                  <>
                    <tr key={r.id} className="border-b border-black/5 hover:bg-[#0a0a0a]/[0.02]">
                      <td className="px-4 py-3 text-xs font-mono whitespace-nowrap">{fmtTs(r.ts)}</td>
                      <td className="px-4 py-3 text-sm">
                        <div className="text-[#0a0a0a] font-semibold">{r.actor_name || "—"}</div>
                        <div className="text-[10px] font-mono uppercase tracking-[0.1em] text-[#9a9a9a]">{r.actor_type}</div>
                      </td>
                      <td className="px-4 py-3">
                        <span className={`px-2 py-0.5 text-[10px] font-mono uppercase tracking-[0.1em] rounded ${ACTION_TONE[r.action] || "bg-zinc-100 text-zinc-700"}`}>{r.action}</span>
                      </td>
                      <td className="px-4 py-3 text-xs font-mono text-[#4a4a4a]">{r.entity_type}</td>
                      <td className="px-4 py-3 text-sm text-[#0a0a0a]">{r.summary || "—"}</td>
                      <td className="px-4 py-3 text-right">
                        {hasDetails && (
                          <button onClick={() => setExpanded((s) => ({ ...s, [r.id]: !s[r.id] }))}
                            className="text-[#4a4a4a] hover:text-[#ff5722]">
                            <ChevronDown className={`h-4 w-4 transition-transform ${open ? "rotate-180" : ""}`} />
                          </button>
                        )}
                      </td>
                    </tr>
                    {open && hasDetails && (
                      <tr className="bg-[#f7f6f3]">
                        <td colSpan={6} className="px-6 py-3">
                          <pre className="text-[11px] font-mono text-[#0a0a0a] whitespace-pre-wrap break-all">{JSON.stringify(r.meta, null, 2)}</pre>
                        </td>
                      </tr>
                    )}
                  </>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </div>
  );
}

function Select({ icon, value, onChange, options, prefix }) {
  return (
    <div className="relative">
      {icon && <span className="absolute left-2.5 top-1/2 -translate-y-1/2 text-[#9a9a9a] pointer-events-none">{icon}</span>}
      <select value={value} onChange={(e) => onChange(e.target.value)}
        className="font-mono text-xs border border-black/15 rounded-full pl-8 pr-7 py-1.5 outline-none focus:border-[#ff5722] appearance-none bg-white capitalize">
        {options.map((o) => (
          <option key={o} value={o}>{prefix}: {o === "all" ? "All" : o.replace(/_/g, " ")}</option>
        ))}
      </select>
    </div>
  );
}
